import { callRpc, createRpcError } from './base';
import { ensureArray } from './util';
import { isRecord, optionalNumber, optionalString } from '../../utils/typeGuards';
import type {
  URLCheckRow,
  URLHealthSummary,
  BrokenURL,
  BulkValidationResult,
  URLValidatorResult
} from '../../types/url-validation';

const mapValidationResult = (raw: unknown): URLCheckRow => {
  if (!isRecord(raw)) {
    throw createRpcError('INVALID_URL_VALIDATION_RESULT', 'URL validation result is not an object', { raw });
  }

  return {
    url_type: optionalString(raw.url_type) ?? 'unknown',
    url: optionalString(raw.url) ?? '',
    status_code: optionalNumber(raw.status_code) ?? 0,
    response_time_ms: optionalNumber(raw.response_time_ms) ?? 0,
    is_accessible: Boolean(raw.is_accessible),
    error_message: optionalString(raw.error_message),
    redirect_url: optionalString(raw.redirect_url),
    redirect_count: optionalNumber(raw.redirect_count) ?? 0,
    severity: optionalString(raw.severity) ?? 'unknown',
    validation_id: optionalString(raw.validation_id) ?? optionalString(raw.id) ?? ''
  };
};

const mapHealthSummary = (raw: unknown): URLHealthSummary => {
  if (!isRecord(raw)) {
    throw createRpcError('INVALID_URL_HEALTH_SUMMARY', 'URL health summary is not an object', { raw });
  }

  return {
    total_urls: optionalNumber(raw.total_urls) ?? 0,
    accessible_urls: optionalNumber(raw.accessible_urls) ?? 0,
    broken_urls: optionalNumber(raw.broken_urls) ?? 0,
    unknown_status: optionalNumber(raw.unknown_status) ?? 0,
    avg_response_time_ms: optionalNumber(raw.avg_response_time_ms) ?? 0,
    critical_issues: optionalNumber(raw.critical_issues) ?? 0,
    high_issues: optionalNumber(raw.high_issues) ?? 0,
    medium_issues: optionalNumber(raw.medium_issues) ?? 0,
    low_issues: optionalNumber(raw.low_issues) ?? 0,
    last_check_within_hour: optionalNumber(raw.last_check_within_hour) ?? 0,
    last_check_within_day: optionalNumber(raw.last_check_within_day) ?? 0,
    last_check_older: optionalNumber(raw.last_check_older) ?? 0,
    ssl_issues: optionalNumber(raw.ssl_issues) ?? 0
  };
};

const mapBrokenUrl = (raw: unknown): BrokenURL => {
  if (!isRecord(raw)) {
    throw createRpcError('INVALID_BROKEN_URL', 'Broken URL entry is not an object', { raw });
  }

  return {
    validation_id: optionalString(raw.validation_id) ?? '',
    program_id: optionalString(raw.program_id) ?? '',
    program_name: optionalString(raw.program_name) ?? 'Unknown',
    awarding_organization: optionalString(raw.awarding_organization) ?? 'Unknown',
    url_type: optionalString(raw.url_type) ?? 'unknown',
    url: optionalString(raw.url) ?? '',
    status_code: optionalNumber(raw.status_code) ?? 0,
    error_message: optionalString(raw.error_message),
    last_checked: optionalString(raw.last_checked) ?? '',
    consecutive_failures: optionalNumber(raw.consecutive_failures) ?? 0,
    severity: optionalString(raw.severity) ?? 'medium',
    response_time_ms: optionalNumber(raw.response_time_ms) ?? 0,
    is_ssl_issue: Boolean(raw.is_ssl_issue),
    priority_score: optionalNumber(raw.priority_score) ?? 0
  };
};

const mapBulkResult = (raw: unknown): BulkValidationResult => {
  if (!isRecord(raw)) {
    throw createRpcError('INVALID_BULK_URL_RESULT', 'Bulk validation result is not an object', { raw });
  }

  return {
    program_id: optionalString(raw.program_id) ?? '',
    program_name: optionalString(raw.program_name) ?? 'Unknown',
    completed: Boolean(raw.completed),
    total_urls: optionalNumber(raw.total_urls) ?? 0,
    accessible_urls: optionalNumber(raw.accessible_urls) ?? 0,
    broken_urls: optionalNumber(raw.broken_urls) ?? 0,
    processing_time_ms: optionalNumber(raw.processing_time_ms) ?? 0,
    validation_timestamp: optionalString(raw.validation_timestamp) ?? new Date().toISOString()
  };
};

export const validateProgramUrls = async (
  programId: string,
  timeoutMs: number,
  followRedirects: boolean
): Promise<URLCheckRow[]> => {
  return await callRpc('validate_program_urls', {
    params: {
      p_program_id: programId,
      p_timeout_ms: timeoutMs,
      p_follow_redirects: followRedirects
    },
    transform: (payload) =>
      ensureArray(payload, 'INVALID_URL_VALIDATION_RESPONSE', 'URL validation RPC returned invalid data')
        .map(mapValidationResult)
  });
};

export const getUrlHealthSummary = async (): Promise<URLHealthSummary[]> => {
  return await callRpc('get_url_health_summary', {
    transform: (payload) =>
      ensureArray(payload, 'INVALID_URL_HEALTH_RESPONSE', 'URL health summary RPC returned invalid data')
        .map(mapHealthSummary)
  });
};

export const getBrokenUrls = async (
  severity: string,
  limit: number
): Promise<BrokenURL[]> => {
  return await callRpc('get_broken_urls', {
    params: {
      p_severity_filter: severity,
      p_limit: limit
    },
    transform: (payload) =>
      ensureArray(payload, 'INVALID_BROKEN_URL_RESPONSE', 'Broken URL RPC returned invalid data')
        .map(mapBrokenUrl)
  });
};

export const bulkValidateUrls = async (
  programIds: string[],
  urlTypes: string[]
): Promise<BulkValidationResult[]> => {
  return await callRpc('bulk_validate_urls', {
    params: {
      p_program_ids: programIds,
      p_url_types: urlTypes
    },
    transform: (payload) =>
      ensureArray(payload, 'INVALID_BULK_URL_RESPONSE', 'Bulk URL validation RPC returned invalid data')
        .map(mapBulkResult)
  });
};

/**
 * Get the latest semantic URL validation result for a program
 * This queries activity_logs for the most recent URL-Validator agent result
 */
export const getLatestUrlValidation = async (programId: string): Promise<URLValidatorResult | null> => {
  return await callRpc('get_latest_url_validation', {
    params: { p_program_id: programId },
    transform: (payload) => {
      if (!payload || !isRecord(payload)) {
        return null;
      }

      // If recommendation is 'not_run', return null
      if (payload.recommendation === 'not_run') {
        return null;
      }

      return payload as unknown as URLValidatorResult;
    },
    successMeta: (result) => ({
      program_id: programId,
      has_validation: result !== null,
      recommendation: result?.recommendation
    })
  });
};
